import { ApplicationRef, Component, Input, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { FormatString } from '../../../../../../../shared/constant/format-string';
import { TbcEnum } from '../../../../../../../shared/Container/enum/depot/TbcEnum';
import { JobTypeEnum } from '../../../../../../../shared/Container/enum/JobTypeEnum';
import { AbstractContainerJobTypeHelper } from '../../../../../../../shared/Container/Helper/AbstractContainerJobTypeHelper';
import { Port } from '../../../../../../../shared/Container/model/depots/Port';
import { VesselVisit } from '../../../../../../../shared/Container/model/VesselVisit';
import { dateGreaterThanValidator } from '../../../../../../../shared/validators/date-greater-than.validator';
import { dateLessThanValidator } from '../../../../../../../shared/validators/date-less-than.validator';
import dayjs from 'dayjs';
import { PortType } from '../../../../../../../shared/Container/enum/PortType';
import { ContainerService } from '../../../../../../../shared/Container/container.service';
import { CommonModule } from '@angular/common';
import { NgOptionComponent, NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { CustomDateRangePickerComponent } from '../../../../../../../shared/components/custom-date-range-picker/custom-date-range-picker.component';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-container-port-data',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgSelectModule, CustomDateRangePickerComponent],
  templateUrl: './container-port-data.component.html',
  styleUrl: './container-port-data.component.scss'
})
export class ContainerPortDataComponent {

  @Input()
  formGroup!: FormGroup;

  @Input()
  jobType: JobTypeEnum | null = null;

  @Input()
  isArrivingLeg: boolean = false;

  @Input()
  resetValue: boolean = true;

  abstractContainerJobTypeHelper!: AbstractContainerJobTypeHelper;

  // Lookup data
  availablePorts: Port[] = [];
  availableSeaPorts: Port[] = [];
  availableRailTerminals: Port[] = [];

  //Display data
  onwardPorts: Port[] = [];

  availableVessels: string[] = [];
  vesselVisits: VesselVisit[] = [];
  selectedVesselVisit: VesselVisit | null = null;

  vesselLoading: boolean = false;
  vesselItems?: Observable<string[]>;
  vesselInput: Subject<string> = new Subject<string>();

  // Binding vars
  showLandPort = (): boolean => this.formGroup && this.formGroup.value.isUsingInlandPort;
  displayOnwardPort: boolean = false;

  tbcVessel: VesselVisit = {
    vesselName: TbcEnum.TBC,
    portCode: '',
    arrivalDateTime: null,
    departureDateTime: null,
    receivalCommenceDateTime: null,
    receivalCutoffDateTime: null,
    scheduledVesselVisitId: 0
  }

  constructor(private containerService: ContainerService, private readonly applicationRef: ApplicationRef) { }

  getFormPartial = (formPath: string) => this.formGroup.get(formPath) as FormControl;

  resetForm() {
    this.formGroup.controls["originPort"].clearValidators();
    this.formGroup.controls["vessel"].clearValidators();
    this.formGroup.controls["fromEtaEtdDate"].clearValidators();
    this.formGroup.controls["receivalCommenceDate"].clearValidators();
    this.formGroup.controls["receivalCutoffDate"].clearValidators();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['formGroup']) {
      this.originPortChanged();
    }
  }

  ngOnInit(): void {
    this.abstractContainerJobTypeHelper = new AbstractContainerJobTypeHelper();

    this.containerService.getContainerPorts()
      .subscribe((ports: any[]) => {
        this.availablePorts = ports;
        this.availableSeaPorts = ports.filter(port => port.portTypes.includes('Sea'));
        this.availableRailTerminals = ports.filter(port => port.portTypes.includes('Rail'));

        if (this.originPort() && this.onwardPorts.length === 0) {
          this.repopulateLandPorts();
        }
      });

    if (this.formGroup) {
      if (this.formGroup.get('originPort')) {
        this.formGroup.get('originPort')!.valueChanges.subscribe(() => this.originPortChanged());
      }
      if (this.formGroup.get('vessel')) {
        this.formGroup.get('vessel')!.valueChanges.subscribe((value) => this.vesselChange(value));
      }
      this.formGroup.get('isUsingInlandPort')!.valueChanges.subscribe(() => this.isUsingInlandPortChanged());
    }

    this.resetValue = true;
  }

  isUsingInlandPortChanged() {
    this.repopulateLandPorts();
  }


  vesselChange(vessel: any) {
    if (this.formGroup.get('fromEtaEtdDate')) {
      if (vessel) {
        if (vessel.vesselName === TbcEnum.TBC || vessel.scheduledVesselVisitId === 0) {
          this.formGroup.get('fromEtaEtdDate')!.clearValidators();
          this.formGroup.get('fromEtaEtdDate')!.patchValue(null);
        } else {
          this.formGroup.get('fromEtaEtdDate')!.setValidators(Validators.required);

          let convertedArrivalDateTime = vessel.arrivalDateTime ? new Date(vessel.arrivalDateTime) : null;

          if (!this.isInboundJob()) {
            const convertedReceivalCommenceDateTime = vessel.receivalCommenceDateTime ? new Date(vessel.receivalCommenceDateTime) : null;
            this.formGroup.get('receivalCommenceDate')!.patchValue(convertedReceivalCommenceDateTime);

            const convertedReceivalCutoffDateTime = vessel.receivalCutoffDateTime ? new Date(vessel.receivalCutoffDateTime) : null;
            this.formGroup.get('receivalCutoffDate')!.patchValue(convertedReceivalCutoffDateTime);

            convertedArrivalDateTime = vessel.departureDateTime ? new Date(vessel.departureDateTime) : null;
          }
          this.formGroup.get('fromEtaEtdDate')!.patchValue(convertedArrivalDateTime);
        }
      } else {
        this.formGroup.get('fromEtaEtdDate')!.patchValue(null);
        this.formGroup.get('receivalCommenceDate')!.patchValue(null);
        this.formGroup.get('receivalCutoffDate')!.patchValue(null);
      }
    }

    this.formGroup.updateValueAndValidity();
  }

  originPortChanged() {
    if (this.resetValue) {
      this.formGroup.controls['vessel'].patchValue(this.tbcVessel);
      this.formGroup.controls['isUsingInlandPort'].patchValue(false);
      this.formGroup.controls['onwardPort'].patchValue(null);

    }

    this.formGroup.controls['originPort'].setValidators(Validators.required);

    this.repopulateLandPorts();
    if (this.originPort()) {
      this.containerService.getScheduledVessels(this.originPort().code)
        .subscribe((vesselVisits: any[]) => {
          this.vesselVisits = [this.tbcVessel, ...vesselVisits];
          this.vesselVisits = this.vesselVisits.map(v => {
            if (v.vesselName === TbcEnum.TBC) {
              return {
                ...v,
                label: `${v.vesselName}`
              }
            } else {
              return {
                ...v,
                label: `${v.vesselName} - ${this.formatVesselDate(v)}`
              }
            }
          });
        });
    }
  }

  isLandPort = () => this.isRailJob() || !this.originPort() || this.originPort().onwardPorts.length === 0;
  isInboundJob = () => this.abstractContainerJobTypeHelper.isInboundJob(this.jobType);
  isSeaJob = () => this.abstractContainerJobTypeHelper.isSeaJob(this.jobType);
  isRailJob = () => this.abstractContainerJobTypeHelper.isRailJob(this.jobType);
  getDirectionalEstimationString = () => this.isInboundJob() || this.isArrivingLeg ? 'ETA' : 'ETD';
  getVesselType = () => this.isSeaJob()
    ? 'Vessel'
    : 'Train / Wagon';

  originPort(): Port {
    return this.formGroup.value.originPort as Port;
  }

  formatVesselDate(vesselVisit: VesselVisit) {
    if (vesselVisit.vesselName === TbcEnum.TBC) {
      return null;
    }

    if (!vesselVisit.arrivalDateTime || !vesselVisit.departureDateTime) {
      return null;
    }

    let formattedDate = new Date(vesselVisit.arrivalDateTime);
    if (!this.isInboundJob()) {
      formattedDate = new Date(vesselVisit.departureDateTime);
    }
    return dayjs(formattedDate).format(FormatString.bryntumDate);
  }

  addVesselNameTag(name: string): VesselVisit{
    return { vesselName: name, arrivalDateTime : null, departureDateTime : null, receivalCommenceDateTime:null, receivalCutoffDateTime: null, portCode : "", scheduledVesselVisitId : 0 };
  }

  onVesselVisitChanged() {
    if (this.selectedVesselVisit) {
      if (this.selectedVesselVisit.vesselName === TbcEnum.TBC) {
        this.formGroup.controls['fromEtaEtdDate']!.clearValidators();
        this.formGroup.controls['fromEtaEtdDate'].patchValue(null);
      } else {
        this.formGroup.controls['fromEtaEtdDate']!.setValidators(Validators.required);

        const convertedArrivalDateTime = this.selectedVesselVisit.arrivalDateTime ? new Date(this.selectedVesselVisit.arrivalDateTime) : null;
        this.formGroup.controls['fromEtaEtdDate'].patchValue(convertedArrivalDateTime);
      }
    }
    else {
      this.formGroup.controls['fromEtaEtdDate'].patchValue(null);
    }
  }

  onFullRequestedDeliveryDateChange(value: any) {
    if (value) {
      const fromEtaEtdDate = this.formGroup.controls['fromEtaEtdDate']!.value;
      this.formGroup.controls['fullRequestedDeliveryDate']!.clearValidators();
      if (fromEtaEtdDate) {
        const formatedFromEtaEtdDate = new Date(fromEtaEtdDate);
        if (this.isInboundJob() || this.isArrivingLeg) {
          this.formGroup.controls['fullRequestedDeliveryDate']!.setValidators([dateLessThanValidator(formatedFromEtaEtdDate)]);
        }else{
          this.formGroup.controls['fullRequestedDeliveryDate']!.setValidators([dateGreaterThanValidator(formatedFromEtaEtdDate)]);
        }
      } else {
        this.formGroup.controls['fullRequestedDeliveryDate']!.setValidators([]);
      }

      this.formGroup.controls['fullRequestedDeliveryDate']!.updateValueAndValidity();
    }
  }

  onReceivalCommenceDateChange(value: any) {
    if (value) {
      value.setHours(0, 0, 0, 0);
      const receivalCutoffDate = this.formGroup.controls['receivalCutoffDate']!.value;
      this.formGroup.controls['receivalCommenceDate']!.clearValidators();
      if (receivalCutoffDate) {
        this.formGroup.controls['receivalCommenceDate']!.setValidators([dateGreaterThanValidator(receivalCutoffDate)]);
      }
      this.formGroup.controls['receivalCommenceDate']!.updateValueAndValidity();
    }
  }

  onReceivalCutoffDateChange(value: any) {
    if (value) {
      value.setHours(0, 0, 0, 0);
      const receivalCommenceDate = this.formGroup.controls['receivalCommenceDate']!.value;
      const fromEtaEtdDate = this.formGroup.controls['fromEtaEtdDate']!.value;
      this.formGroup.controls['receivalCutoffDate']!.clearValidators();
      if (fromEtaEtdDate) {
        this.formGroup.controls['receivalCutoffDate']!.setValidators([dateGreaterThanValidator(fromEtaEtdDate), dateLessThanValidator(receivalCommenceDate)]);
      }
      this.formGroup.controls['receivalCutoffDate']!.updateValueAndValidity();
    }
  }

  onVesselEtaEtdDateChange(value: any) {
      const fullRequestedDeliveryDateControl = this.formGroup.controls['fullRequestedDeliveryDate'];

      if (fullRequestedDeliveryDateControl) {
          fullRequestedDeliveryDateControl!.clearValidators();

        if (value) {

              if (this.isInboundJob() || this.isArrivingLeg) {
                fullRequestedDeliveryDateControl!.setValidators([dateLessThanValidator(value)]);
              } else {
                fullRequestedDeliveryDateControl!.setValidators([dateGreaterThanValidator(value)]);
              }
          } else {
          fullRequestedDeliveryDateControl!.setValidators([]);
          }

          fullRequestedDeliveryDateControl!.updateValueAndValidity();
      }
  }

  showIsInlandPortOption = () => this.originPort() !== null && !this.isRailJob() && this.originPort().onwardPorts && this.originPort().onwardPorts.length > 0;

  repopulateLandPorts() {
    if (this.availablePorts.length === 0) {
      return;
    }
    this.onwardPorts = [];
    const originPort = this.originPort();
    if (originPort && originPort.onwardPorts) {
      originPort.onwardPorts.forEach(op => {
        const port = (<Port>this.availablePorts.find(v => v.code === op.code));
        this.onwardPorts.push(port);
      });
    }

    if (this.formGroup.value.onwardPort === null && this.onwardPorts.length > 0) {
      this.formGroup.patchValue({ onwardPort: this.onwardPorts[0] });
    }
  }

  getFilteredVessels(term: string): string[] {
    if (term.length === 0) {
      return this.availableVessels;
    }
    return this.availableVessels.filter(n => n.includes(term));
  }
}
